import { useEffect, useState } from "react";
import { useTranslation } from "@ikea-customer-self-serve/gatsby-plugin-translations";
import { SelectedItemType, SparePart } from "../interfaces/Item";
import { sparePartService } from "../services";
import { useErrorCode } from "./useErrorCode";
import { useDebounce } from "./useDebounce";

interface SearchState {
  loading: boolean;
  loadingParameter: boolean;
  search: string;
  items: SparePart[];
  selectedItem: SelectedItemType;
}
interface SearchActions {
  setSearch: (search: string) => void;
  setSelectedItem: (item: SelectedItemType) => void;
}
export const useSearch = (
  initialArtId: string,
  initialItemId: string
): [SearchState, SearchActions] => {
  const { t } = useTranslation();
  const errorContext = useErrorCode();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, CSS_CONFIG.DEBOUNCEDELAYMS);
  const [items, setItems] = useState<SparePart[]>([]);
  const [selectedItem, setSelectedItem] = useState<SelectedItemType>();
  const [loading, setIsLoading] = useState(false);
  const [loadingParameter, setIsLoadingParameter] = useState(true);

  useEffect(() => {
    (async (): Promise<void> => {
      try {
        if (initialArtId) {
          const result = await sparePartService.fetchSparePartItemsFromArticleId(
            initialArtId
          );
          setItems(result);
        } else if (initialItemId) {
          const result = await sparePartService.fetchSparePartId(
            parseInt(initialItemId, 10)
          );
          setSelectedItem(result);
        }
      } catch (e) {
        // Do stuff
      } finally {
        setIsLoadingParameter(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async (): Promise<void> => {
      try {
        setIsLoading(true);
        const result = await sparePartService.searchSpareParts(debouncedSearch);
        setItems(result);
      } catch (e) {
        errorContext.setErrorCode({
          status: e.status,
        });
      } finally {
        setIsLoading(false);
      }
    })();
  }, [debouncedSearch]);

  return [
    {
      search,
      loading,
      loadingParameter,
      selectedItem,
      items,
    },
    {
      setSearch,
      setSelectedItem,
    },
  ];
};
